import React from 'react';
import { debounce } from 'lodash';
import { BasicForm } from './BasicForm.jsx';
import { FormLabel } from '../FormLabel.jsx';
import { FormErrorMessage } from '../FormErrorMessage.jsx';

import * as zipcode from '../../../../../../utils/zipcode';
import { zipcodeRE } from '../../../../../../utils/validations/validations.js';

export class AddressForm extends BasicForm {
  componentDidMount () {
    // mark field with initial_value as touched
    this.checkValuesToTouch();
  }

  checkValuesToTouch () {
    const { touch, nameMap } = this.props;

    if (this.inputMap.zipcodeZone.input.value) {
      touch(nameMap.zipcodeZone);
    }
    if (this.inputMap.zipcodeArea.input.value) {
      touch(nameMap.zipcodeArea);
    }
    if (this.inputMap.prefecture.input.value) {
      touch(nameMap.prefecture);
    }
    if (this.inputMap.city.input.value) {
      touch(nameMap.city);
    }
    if (this.inputMap.street.input.value) {
      touch(nameMap.street);
    }

    if (this.hasExtended) {
      if (this.inputMap.extended.input.value) {
        touch(nameMap.extended);
      }
    }
  }

  handleChangeZipcode = (cb) => (e) => {
    cb(e);
    this.debounced_autoFillByZipcode();
  }

  autoFillByZipcode = () => {
    // Not to run if value format is invalid
    if (!this.isZipcodeValid) {
      return;
    }

    // Not to run if it's disabled in order to avoid overriding values,
    // like when form just got submitted
    if (this.props.disabled) {
      return;
    }

    zipcode.convertToAddress(this.zipcodeInputValue)
      .then((data) => {
        // Seems to be what yubinbango library returns for street is wrongly named,
        // since the values are names of town.
        // Combine data.locality and data.street and put it into city input,
        // and put the data.extended into street
        // https://www.pivotaltracker.com/story/show/173442798
        this.inputMap.prefecture.input.onChange(data.region);
        this.inputMap.city.input.onChange(data.locality + data.street);
        this.inputMap.street.input.onChange(data.extended);
      });
  }
  debounced_autoFillByZipcode = debounce(this.autoFillByZipcode, 1000);

  render () {
    const { object, disabled } = this.props;
    const im = this.inputMap;

    return (
      <div className="cu-ui-form-group">
        <div className="cu-ui-form-row" data-testid="zipcodeRow">
          <FormLabel
            valid={im.zipcodeZone.meta.valid && im.zipcodeArea.meta.valid}
            label={object.data.zipcode.label}
            required={object.data.zipcode.validations.required}
          />
          <div className="cu-ui-address-zipcode">
            <input
              { ...im.zipcodeZone.input }
              data-testid="zipcodeZoneInput"
              className="cu-ui-input"
              type="number"
              placeholder={object.data.zipcode.zone.placeholder}
              disabled={disabled}
              onChange={this.handleChangeZipcode(im.zipcodeZone.input.onChange)}
              onFocus={this.handleFocusInput(im.zipcodeZone.input.onFocus)}
              onBlur={this.handleBlurInput(im.zipcodeZone.input.onBlur)}
            />
            <div className="cu-ui-address-zipcode__separator"/>
            <input
              { ...im.zipcodeArea.input }
              data-testid="zipcodeAreaInput"
              className="cu-ui-input"
              type="number"
              placeholder={object.data.zipcode.area.placeholder}
              disabled={disabled}
              onChange={this.handleChangeZipcode(im.zipcodeArea.input.onChange)}
              onFocus={this.handleFocusInput(im.zipcodeArea.input.onFocus)}
              onBlur={this.handleBlurInput(im.zipcodeArea.input.onBlur)}
            />
          </div>
          <FormErrorMessage
            error={im.zipcodeZone.meta.error || im.zipcodeArea.meta.error}
            touched={im.zipcodeZone.meta.touched && im.zipcodeArea.meta.touched}
          />
        </div>

        <div className="cu-ui-form-row" data-testid="prefectureRow">
          <FormLabel
            valid={this.inputMap.prefecture.meta.valid}
            label={object.data.prefecture.label}
            required={object.data.prefecture.validations.required}
          />
          <input
            { ...this.inputMap.prefecture.input }
            data-testid="prefectureInput"
            placeholder={object.data.prefecture.placeholder}
            className="cu-ui-input"
            type="text"
            disabled={disabled}
            onFocus={this.handleFocusInput(im.prefecture.input.onFocus)}
            onBlur={this.handleBlurInput(im.prefecture.input.onBlur)}
          />
          <FormErrorMessage
            error={this.inputMap.prefecture.meta.error}
            touched={this.inputMap.prefecture.meta.touched}
          />
        </div>

        <div className="cu-ui-form-row" data-testid="cityRow">
          <FormLabel
            valid={this.inputMap.city.meta.valid}
            label={object.data.city.label}
            required={object.data.city.validations.required}
          />
          <input
            { ...this.inputMap.city.input }
            data-testid="cityInput"
            placeholder={object.data.city.placeholder}
            className="cu-ui-input"
            type="text"
            disabled={disabled}
            onFocus={this.handleFocusInput(im.city.input.onFocus)}
            onBlur={this.handleBlurInput(im.city.input.onBlur)}
          />
          <FormErrorMessage
            error={this.inputMap.city.meta.error}
            touched={this.inputMap.city.meta.touched}
          />
        </div>

        <div className="cu-ui-form-row" data-testid="streetRow">
          <FormLabel
            valid={this.inputMap.street.meta.valid}
            label={object.data.street.label}
            required={object.data.street.validations.required}
          />
          <input
            { ...this.inputMap.street.input }
            data-testid="streetInput"
            placeholder={object.data.street.placeholder}
            className="cu-ui-input"
            type="text"
            disabled={disabled}
            onFocus={this.handleFocusInput(im.street.input.onFocus)}
            onBlur={this.handleBlurInput(im.street.input.onBlur)}
          />
          <FormErrorMessage
            error={this.inputMap.street.meta.error}
            touched={this.inputMap.street.meta.touched}
          />
        </div>

        {this.hasExtended && (
          <div className="cu-ui-form-row" data-testid="extendedRow">
            <FormLabel
              valid={this.inputMap.extended.meta.valid}
              label={object.data.extended.label}
              required={object.data.extended.validations.required}
            />
            <input
              { ...this.inputMap.extended.input }
              data-testid="extendedInput"
              placeholder={object.data.extended.placeholder}
              className="cu-ui-input"
              type="text"
              disabled={disabled}
              onFocus={this.handleFocusInput(im.extended.input.onFocus)}
              onBlur={this.handleBlurInput(im.extended.input.onBlur)}
            />
            <FormErrorMessage
              error={this.inputMap.extended.meta.error}
              touched={this.inputMap.extended.meta.touched}
            />
          </div>
        )}
      </div>
    );
  }

  get hasExtended () {
    return !!this.props.nameMap.extended;
  }

  get inputMap () {
    return {
      zipcodeZone: this.props[this.props.nameMap.zipcodeZone],
      zipcodeArea: this.props[this.props.nameMap.zipcodeArea],
      prefecture: this.props[this.props.nameMap.prefecture],
      city: this.props[this.props.nameMap.city],
      street: this.props[this.props.nameMap.street],
      extended: this.props[this.props.nameMap.extended],
    };
  }

  get zipcodeInputValue () {
    return this.inputMap.zipcodeZone.input.value + '-' + this.inputMap.zipcodeArea.input.value;
  }

  get isZipcodeValid () {
    return zipcodeRE.test(this.zipcodeInputValue);
  }
}
